import { getStyle, isElementDomNode, omitExtraAttributes, cx, joinStyles } from '@remirror/core';
import { CodeBlockExtension } from 'remirror/extensions';
import refactor from 'refractor/core.js';
import React from 'react';

export class CodeblockExtension extends CodeBlockExtension {
  constructor(options) {
    super(options);
  }

  get name() {
    return 'codeBlock';
  }

  init() {
    this.registerLanguages();
  }

  createNodeSpec(extra, override) {
    const githubHighlightRegExp = /highlight-(?:text|source)-([\da-z]+)/;
    return {
      content: 'text*',
      marks: '',
      defining: true,
      draggable: false,
      ...override,
      code: true,
      attrs: {
        ...extra.defaults(),
        language: { default: this.options.defaultLanguage },
        wrap: { default: this.options.defaultWrap },
        lineNumbers: { default: this.options.defaultLineNumbers },
      },
      parseDom: [
        {
          tag: 'div.highlight',
          preserveWhitespace: 'full',
          getAttrs: (node) => {
            if (!isElementDomNode(node)) return false;

            const codeElement = node.querySelector('code');
            if (!isElementDomNode(codeElement)) return false;

            const wrap = getStyle(codeElement, 'white-space') === 'pre-wrap';
            const language = node.className.match(githubHighlightRegExp)?.[1]?.replace('language-', '');
            const lineNumbers = node.getAttribute('data-line-numbers') === 'true';

            return { ...extra.parse(node), language, wrap, lineNumbers };
          },
        },
        {
          tag: 'pre',
          preserveWhitespace: 'full',
          getAttrs: (node) => {
            if (!isElementDomNode(node)) {
              return false;
            }

            const codeElement = node.querySelector('code');

            if (!isElementDomNode(codeElement)) {
              return false;
            }

            const wrap = getStyle(codeElement, 'white-space') === 'pre-wrap';
            const language = this.options.getLanguageFromDom(codeElement, node);
            const lineNumbers = node.getAttribute('data-line-numbers') === 'true';
            return { ...extra.parse(node), language, wrap, lineNumbers };
          },
        },
        ...(override.parseDOM ?? []),
      ],
      toDOM: (node) =>
        (() => {
          const { language, wrap, lineNumbers } = omitExtraAttributes(node.attrs, extra);
          const { style: _, ...extraAttrs } = extra.dom(node);
          let style = extraAttrs.style;

          if (wrap) {
            style = joinStyles({ whiteSpace: 'pre-wrap', wordBreak: 'break-all' }, style);
          }

          const attributes = {
            spellcheck: 'false',
            ...extraAttrs,
            class: cx(extraAttrs.class, `language-${language}`),
            'data-line-numbers': lineNumbers,
          };

          return ['pre', attributes, ['code', { class: `language-${language}`, style }, 0]];
        })(),
    };
  }

  registerLanguages() {
    for (const language of this.options.supportedLanguages) {
      refactor.register(language);
    }
  }
}

export default CodeblockExtension;
